import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Calendar, Users, MapPin, Sun, Fish, Compass, ChevronDown, ChevronUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import TripBookingForm from '../components/TripBookingForm';
import ContactBanner from '../components/ContactBanner';
import { KayakBadge } from '../components/KayakBadge';
import { TripInquiryModal } from '../components/TripInquiryModal';
import { formatItemPrice } from '../lib/currency';
import { usePageHeader } from '../hooks/usePageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TripsPage = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [inquiryTrip, setInquiryTrip] = useState(null);
  // Tracks per-trip selected accommodation option index (only relevant when trip.accommodation_options is set).
  const [accommodationByTrip, setAccommodationByTrip] = useState({});
  // Tracks per-trip chosen destination (only relevant when trip.destinations has 2+ entries).
  const [destinationByTrip, setDestinationByTrip] = useState({});
  
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const header = usePageHeader('trips', { heading: 'Dive Trips', subheading: 'Destinations that work. Manageable groups. Straightforward planning.' });

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const response = await axios.get(`${API_URL}/trips`);
      setTrips(response.data);
    } catch (error) {
      console.error('Failed to fetch trips:', error);
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  // (The dedicated /trips/:id route makes deep-link scrolling unnecessary —
  // shared links land directly on TripDetailPage.)

  const shareTrip = async (trip) => {
    const opts = trip.accommodation_options || [];
    const idx = accommodationByTrip[trip.id] ?? 0;
    const chosen = opts[idx];
    const basisLabel = chosen
      ? ({ pps: 'pps', pp: 'pp', single: 'single' }[chosen.pricing_basis || 'pps'] || 'pps')
      : 'pps';
    const priceVal = chosen?.price ?? trip.price;
    const fromPrefix = (opts.length > 1 || trip.show_starting_from) ? 'from ' : '';
    // Share via the server-rendered OG endpoint so WhatsApp / FB / iMessage get
    // a proper preview (correct title, description and hero image). The endpoint
    // redirects real visitors to /trips/:id. The `?v=` cache-buster forces
    // WhatsApp to fetch a fresh preview after the admin edits the trip — WA
    // caches previews by exact URL so changing the query string defeats it.
    const apiBase = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
    const ver = encodeURIComponent(trip.updated_at || trip.created_at || Date.now());
    const url = `${apiBase}/api/share/trip/${trip.id}?v=${ver}`;
    const dateLine = trip.inquire_only
      ? 'Dates: TBC — register your interest below'
      : (trip.display_date || `${trip.start_date} - ${trip.end_date}`);
    const priceLine = priceVal
      ? `${fromPrefix}${formatItemPrice(priceVal, trip.currency, { compact: true })} ${basisLabel}${trip.inclusions ? ` · ${trip.inclusions}` : ''}`
      : 'Price on request';
    const text = [
      `🤿 Check out this dive trip with Just Dive!`,
      ``,
      `*${trip.destination}*`,
      dateLine,
      priceLine,
      ``,
      url,
    ].join('\n');

    // Prefer the native share sheet on mobile so users can pick WhatsApp / SMS / Email.
    if (navigator.share) {
      try {
        await navigator.share({ title: trip.destination, text, url });
        return;
      } catch (_) { /* user cancelled, fall through */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — opening WhatsApp…');
    } catch (e) {
      // Older browsers: silent fallback to WhatsApp only.
    }
    const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const handleBookTrip = (trip) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    // Enforce the destination pick BEFORE opening the booking form when the
    // trip is a multi-destination (dropdown) variant. This is a hard gate —
    // the diver's choice must be locked in before payment.
    const dests = trip.destinations || [];
    let selectedDestination = null;
    if (dests.length >= 2) {
      selectedDestination = destinationByTrip[trip.id];
      if (!selectedDestination) {
        toast.error('Please choose a destination first');
        return;
      }
    }
    // If accommodation options exist, pass the chosen one through as the effective price.
    const opts = trip.accommodation_options || [];
    let tripForBooking = trip;
    if (opts.length > 0) {
      const idx = accommodationByTrip[trip.id] ?? 0;
      const chosen = opts[idx] || opts[0];
      tripForBooking = {
        ...trip,
        price: chosen.price,
        selected_accommodation: chosen,
      };
    }
    if (selectedDestination) {
      tripForBooking = {
        ...tripForBooking,
        selected_destination: selectedDestination,
        // Overwrite the display destination so the booking form + cart /
        // email use the diver's chosen location, not the "or" umbrella.
        destination: selectedDestination,
      };
    }
    setSelectedTrip(tripForBooking);
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (bookingData) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Submit medical forms and liability waivers to backend
    try {
      // Primary booker medical form
      const primaryMedicalForm = {
        participant_name: bookingData.formData.primaryName,
        email: bookingData.formData.primaryEmail,
        phone: bookingData.formData.primaryPhone,
        date_of_birth: bookingData.formData.primaryBirthdate,
        emergency_contact: bookingData.formData.primaryEmergencyContact,
        emergency_phone: bookingData.formData.primaryEmergencyPhone,
        trip_id: bookingData.trip.id,
        trip_name: bookingData.trip.destination,
        medical: bookingData.formData.medical,
        signature_name: bookingData.formData.medicalSignatureName,
        signature_date: bookingData.formData.medicalSignatureDate,
        signed: bookingData.formData.medicalSignature
      };
      
      // Primary booker liability waiver
      const primaryWaiver = {
        participant_name: bookingData.formData.primaryName,
        email: bookingData.formData.primaryEmail,
        phone: bookingData.formData.primaryPhone,
        trip_id: bookingData.trip.id,
        trip_name: bookingData.trip.destination,
        accepted: bookingData.formData.waiverAccepted,
        signature_name: bookingData.formData.waiverSignatureName,
        signature_date: bookingData.formData.waiverSignatureDate
      };
      
      // Submit primary forms
      await axios.post(`${API_URL}/medical-forms`, primaryMedicalForm);
      await axios.post(`${API_URL}/liability-waivers`, primaryWaiver);
      
      // Submit additional attendees' forms
      for (const att of bookingData.formData.additionalAttendees) {
        if (att.name) {
          const attMedicalForm = {
            participant_name: att.name,
            email: att.email || bookingData.formData.primaryEmail,
            phone: att.phone || '',
            date_of_birth: att.birthdate,
            emergency_contact: att.emergencyContact,
            emergency_phone: att.emergencyPhone,
            trip_id: bookingData.trip.id,
            trip_name: bookingData.trip.destination,
            medical: att.medical,
            signature_name: att.medicalSignatureName,
            signature_date: att.medicalSignatureDate,
            signed: att.medicalSignature,
            primary_booker: bookingData.formData.primaryName
          };
          
          const attWaiver = {
            participant_name: att.name,
            email: att.email || bookingData.formData.primaryEmail,
            trip_id: bookingData.trip.id,
            trip_name: bookingData.trip.destination,
            accepted: att.waiverAccepted || bookingData.formData.waiverAccepted,
            signature_name: att.waiverSignatureName || att.medicalSignatureName,
            signature_date: att.waiverSignatureDate || att.medicalSignatureDate,
            primary_booker: bookingData.formData.primaryName
          };
          
          await axios.post(`${API_URL}/medical-forms`, attMedicalForm);
          await axios.post(`${API_URL}/liability-waivers`, attWaiver);
        }
      }
    } catch (error) {
      console.error('Error submitting forms:', error);
      // Continue with cart even if form submission fails
    }
    
    const accSuffix = bookingData.trip.selected_accommodation
      ? ` • ${bookingData.trip.selected_accommodation.name}`
      : '';
    // NOTE: `bookingData.trip.destination` was already overwritten to the
    // diver's chosen destination back in handleBookTrip when this is a
    // multi-destination trip. So every downstream reference (cart, order,
    // confirmation email, admin dashboard) uses the specific location.
    const tripItem = {
      id: `trip-${bookingData.trip.id}-${Date.now()}`,
      tripId: bookingData.trip.id,
      name: `Trip: ${bookingData.trip.destination}${accSuffix}`,
      description: `${bookingData.trip.display_date || `${bookingData.trip.start_date} - ${bookingData.trip.end_date}`} • ${bookingData.attendeeCount} ${bookingData.attendeeCount === 1 ? 'person' : 'people'}${accSuffix}${bookingData.gearHireCost > 0 ? ' • Gear Hire Included' : ''}`,
      price: bookingData.totalPrice,
      image_url: bookingData.trip.image_url,
      category: 'trip',
      isTrip: true,
      selectedDestination: bookingData.selectedDestination || bookingData.trip.selected_destination || null,
      bookingDetails: bookingData.formData,
      attendeeCount: bookingData.attendeeCount,
      pricePerPerson: bookingData.trip.price,
      tripPrice: bookingData.tripPrice,
      gearHireCost: bookingData.gearHireCost || 0,
      gearHire: bookingData.gearHire,
      gearSummary: bookingData.gearSummary,
      cateringOpted: !!bookingData.cateringOpted,
      cateringCost: bookingData.cateringCost || 0,
      cateringPricePerPerson: bookingData.cateringPricePerPerson || 0,
      tripDays: bookingData.tripDays,
      medicalForms: {
        primary: {
          responses: bookingData.formData.medical,
          signature: bookingData.formData.medicalSignatureName,
          signatureDate: bookingData.formData.medicalSignatureDate
        },
        additionalAttendees: bookingData.formData.additionalAttendees.map(att => ({
          name: att.name,
          responses: att.medical,
          signature: att.medicalSignatureName,
          signatureDate: att.medicalSignatureDate
        }))
      },
      waiverDetails: {
        accepted: bookingData.formData.waiverAccepted,
        signature: bookingData.formData.waiverSignatureName,
        signatureDate: bookingData.formData.waiverSignatureDate
      }
    };

    addToCart(tripItem, 1);
    toast.success(`${bookingData.trip.destination} trip for ${bookingData.attendeeCount} ${bookingData.attendeeCount === 1 ? 'person' : 'people'} added to cart!`);
    setShowBookingModal(false);
    setSelectedTrip(null);
  };

  const toggleExpanded = (tripId) => {
    setExpandedTrip(expandedTrip === tripId ? null : tripId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#0A2342] mb-4">
            Dive Trips
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            Destinations that work. Manageable groups. Straightforward planning.
          </p>
        </div>

        {trips.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500">No trips available at the moment</p>
            <p className="text-gray-400 mt-2">Check back soon for upcoming dive trips!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {trips.map((trip) => (
              <div
                key={trip.id}
                id={`trip-${trip.id}`}
                className="bg-white border border-gray-100 rounded-lg overflow-hidden hover:border-[#0A2342]/20 hover:shadow-xl transition-all duration-300 scroll-mt-24"
                data-testid={`trip-card-${trip.id}`}
              >
                <div className="overflow-hidden bg-gray-100 relative cursor-pointer flex items-center justify-center max-h-[640px]" onClick={() => toggleExpanded(trip.id)}>
                  <img
                    src={trip.image_url}
                    alt={trip.destination}
                    className="w-auto h-auto max-w-full max-h-[640px] block"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const sib = e.currentTarget.nextElementSibling?.nextElementSibling;
                      if (sib) sib.style.display = 'flex';
                    }}
                  />
                  <div
                    className="hidden items-center justify-center bg-gradient-to-br from-[#0A2342] to-[#1B4F8E] text-white text-center px-6 w-full aspect-video"
                    style={{ display: 'none' }}
                    data-testid={`trip-image-fallback-${trip.id}`}
                  >
                    <div>
                      <p className="text-xs uppercase tracking-widest opacity-70 mb-2">Image pending</p>
                      <p className="font-heading text-2xl font-bold">{trip.destination}</p>
                      <p className="text-xs opacity-60 mt-3">Admin: re-upload trip image</p>
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3">
                    <Badge className="bg-white/90 text-[#0A2342] backdrop-blur-sm">
                      Click for details
                    </Badge>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-2xl font-heading font-semibold text-[#0A2342]">
                        {(trip.destinations && trip.destinations.length >= 2)
                          ? trip.destinations.join(' or ')
                          : trip.destination}
                      </h3>
                      {trip.available_on_pro && (
                        <Badge className="bg-purple-600 text-white text-xs" data-testid={`pro-badge-${trip.id}`}>
                          PRO
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {trip.description}
                  </p>

                  {/* Multi-destination picker — only when this trip runs to 2+ places
                      on the same dates (e.g. Sodwana OR Ponta). Locks the diver's
                      choice before Book. */}
                  {trip.destinations && trip.destinations.length >= 2 && (
                    <div className="mb-4 pb-4 border-b border-gray-100" data-testid={`destination-block-${trip.id}`}>
                      <label className="block text-xs font-semibold text-[#0A2342] mb-2 uppercase tracking-wide">
                        Choose your destination
                      </label>
                      <select
                        value={destinationByTrip[trip.id] ?? ''}
                        onChange={(e) => setDestinationByTrip({ ...destinationByTrip, [trip.id]: e.target.value })}
                        className="w-full border border-gray-200 rounded-md p-2 text-sm bg-white focus:border-[#FF6B35] focus:outline-none"
                        data-testid={`destination-select-${trip.id}`}
                      >
                        <option value="">— Select destination —</option>
                        {trip.destinations.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-gray-500 mt-1">Your choice is locked at booking and paid up-front. Same dates, same price — different reef.</p>
                    </div>
                  )}

                  {/* Expand/Collapse Button */}
                  <button 
                    onClick={() => toggleExpanded(trip.id)}
                    className="flex items-center gap-2 text-[#FF6B35] font-medium text-sm mb-4 hover:underline"
                    data-testid={`expand-trip-${trip.id}`}
                  >
                    {expandedTrip === trip.id ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        View More Details
                      </>
                    )}
                  </button>

                  {/* Expanded Details */}
                  {expandedTrip === trip.id && (
                    <div className="bg-[#F0EFEA] rounded-lg p-4 mb-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                      {/* In a Nutshell — pinned to the top so the diver
                          gets the punch-line before the long-form details.
                          Only shown when the admin has filled it in. */}
                      {trip.nutshell && (
                        <div className="bg-white/70 border-l-4 border-[#FF6B35] rounded p-3 space-y-1" data-testid={`nutshell-block-${trip.id}`}>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-[#FF6B35]">In a Nutshell</p>
                          <p className="text-sm text-[#0A2342] whitespace-pre-line leading-relaxed">{trip.nutshell}</p>
                        </div>
                      )}

                      {/* Catered / Self-Catered badges — both can be shown
                          when the trip offers both. Catered badge includes
                          the extra price if set. */}
                      {(trip.catered || trip.self_catering) && (
                        <div className="flex flex-wrap gap-2" data-testid={`catering-badges-${trip.id}`}>
                          {trip.catered && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/30 text-xs font-semibold">
                              🍴 Catered
                              {(trip.catered_price ?? 0) > 0 && (
                                <span className="text-[10px] font-normal text-gray-600">+R{Number(trip.catered_price).toLocaleString()}</span>
                              )}
                            </span>
                          )}
                          {trip.self_catering && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0A2342]/10 text-[#0A2342] border border-[#0A2342]/30 text-xs font-semibold">
                              🥫 Self-Catered
                            </span>
                          )}
                        </div>
                      )}

                      <h4 className="font-semibold text-[#0A2342] border-b border-[#0A2342]/10 pb-2">Trip Details</h4>

                      <p className="text-gray-600 text-sm whitespace-pre-line">{trip.description}</p>
                      
                      {trip.dive_site && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-[#0A2342] text-sm">Dive Site</p>
                            <p className="text-gray-600 text-sm">{trip.dive_site}</p>
                          </div>
                        </div>
                      )}
                      
                      {trip.sighting_possibilities && (
                        <div className="flex items-start gap-3">
                          <Fish className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-[#0A2342] text-sm">Sighting Possibilities</p>
                            <p className="text-gray-600 text-sm">{trip.sighting_possibilities}</p>
                          </div>
                        </div>
                      )}
                      
                      {trip.typical_weather && (
                        <div className="flex items-start gap-3">
                          <Sun className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-[#0A2342] text-sm">Typical Weather</p>
                            <p className="text-gray-600 text-sm">{trip.typical_weather}</p>
                          </div>
                        </div>
                      )}
                      
                      {trip.area_activities && (
                        <div className="flex items-start gap-3">
                          <Compass className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-[#0A2342] text-sm">What To Do Around The Area</p>
                            <p className="text-gray-600 text-sm">{trip.area_activities}</p>
                          </div>
                        </div>
                      )}
                      
                      {!trip.dive_site && !trip.sighting_possibilities && !trip.typical_weather && !trip.area_activities && (
                        <p className="text-gray-500 text-sm italic">More details coming soon. Contact us for more information about this trip.</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2 mb-4">
                    {trip.inquire_only ? (
                      <div className="flex items-center gap-2 text-sm text-[#FF6B35] font-medium">
                        <Calendar className="w-4 h-4" strokeWidth={1.5} />
                        <span>Dates TBC — register your interest below</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" strokeWidth={1.5} />
                        <span>{trip.display_date || `${trip.start_date} - ${trip.end_date}`}</span>
                      </div>
                    )}
                    {trip.max_participants && !trip.inquire_only && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="w-4 h-4" strokeWidth={1.5} />
                        <span>Max {trip.max_participants} participants</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex flex-col">
                      {(() => {
                        const hasAccomm = trip.accommodation_options && trip.accommodation_options.length > 0;
                        const showStartingFrom = trip.show_starting_from || hasAccomm || trip.destination.toLowerCase().includes('egypt');
                        return showStartingFrom ? (
                          <span className="text-xs text-gray-500 mb-1">starting from</span>
                        ) : null;
                      })()}
                      {(() => {
                        const opts = trip.accommodation_options;
                        const BASIS_LABEL = { pps: 'per person sharing', pp: 'per person', single: 'single (solo room)' };
                        let priceVal = trip.price;
                        // Default to "per person sharing" (pps) when the trip has no
                        // accommodation options — matches the backend default and the
                        // Just Dive pricing convention.
                        let basis = 'pps';
                        if (opts && opts.length > 0) {
                          const idx = accommodationByTrip[trip.id] ?? 0;
                          const chosen = opts[idx] || opts[0];
                          priceVal = chosen.price ?? trip.price;
                          basis = chosen.pricing_basis || 'pps';
                        }
                        return (
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-2xl font-mono-accent font-bold text-[#0A2342]">
                              {priceVal ? formatItemPrice(priceVal, trip.currency, { compact: true }) : 'Price TBC'}
                            </span>
                            {priceVal ? (
                              <span className="text-sm text-gray-500">{BASIS_LABEL[basis] || 'per person sharing'}</span>
                            ) : null}
                          </div>
                        );
                      })()}
                      {trip.included_in_pro && user?.membership_tier === 'pro' && (
                        <div className="text-xs text-[#FF6B35] font-semibold mt-1">
                          Included in Pro
                        </div>
                      )}
                    </div>
                    {trip.inquire_only ? (
                      <Button
                        onClick={() => { setInquiryTrip(trip); setShowInquiryModal(true); }}
                        className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90 shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap"
                        data-testid={`inquire-trip-${trip.id}`}
                      >
                        Inquire Now
                      </Button>
                    ) : (() => {
                      const multiDest = trip.destinations && trip.destinations.length >= 2;
                      const noDestPicked = multiDest && !destinationByTrip[trip.id];
                      const fullyBooked = trip.max_participants && trip.current_participants >= trip.max_participants;
                      let label = 'Book Trip';
                      if (!user) label = 'Sign In to Book';
                      else if (fullyBooked) label = 'Fully Booked';
                      else if (noDestPicked) label = 'Pick a destination';
                      return (
                        <Button
                          onClick={() => handleBookTrip(trip)}
                          disabled={!user || fullyBooked || noDestPicked}
                          className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap"
                          data-testid={`book-trip-${trip.id}`}
                        >
                          {label}
                        </Button>
                      );
                    })()}
                  </div>

                  {trip.accommodation_options && trip.accommodation_options.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100" data-testid={`accommodation-block-${trip.id}`}>
                      <label className="block text-xs font-semibold text-[#0A2342] mb-2 uppercase tracking-wide">
                        Choose your accommodation ({trip.accommodation_options.length} options)
                      </label>
                      <select
                        value={accommodationByTrip[trip.id] ?? 0}
                        onChange={(e) => setAccommodationByTrip({ ...accommodationByTrip, [trip.id]: Number(e.target.value) })}
                        className="w-full border border-gray-200 rounded-md p-2 text-sm bg-white focus:border-[#FF6B35] focus:outline-none"
                        data-testid={`accommodation-select-${trip.id}`}
                      >
                        {trip.accommodation_options.map((opt, i) => {
                          const basisShort = { pps: 'pps', pp: 'pp', single: 'single' }[opt.pricing_basis || 'pps'] || 'pps';
                          return (
                            <option key={i} value={i}>
                              {opt.name} — {formatItemPrice(opt.price, trip.currency, { compact: true })} {basisShort}
                            </option>
                          );
                        })}
                      </select>
                      {(() => {
                        const idx = accommodationByTrip[trip.id] ?? 0;
                        const desc = trip.accommodation_options[idx]?.description;
                        return desc ? <p className="text-xs text-gray-500 mt-2">{desc}</p> : null;
                      })()}
                    </div>
                  )}

                  {trip.inclusions && (
                    <div className="mt-3 text-xs text-gray-600">
                      <span className="font-semibold text-[#0A2342]">Includes:</span> {trip.inclusions}
                    </div>
                  )}

                  {/* Share with your buddies */}
                  <button
                    type="button"
                    onClick={() => shareTrip(trip)}
                    className="mt-4 w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#22c55e] text-white font-semibold text-sm py-2.5 px-4 rounded-md transition-colors shadow-sm hover:shadow"
                    data-testid={`share-trip-${trip.id}`}
                  >
                    <svg viewBox="0 0 32 32" className="w-4 h-4 fill-current" aria-hidden="true">
                      <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.49-1.46-2.19a.63.63 0 0 1-.062-.27c0-.55 1.09-.808 1.09-1.39 0-.118-.025-.222-.075-.32-.215-.418-.625-1.395-.836-1.78-.13-.236-.32-.323-.5-.323a30.66 30.66 0 0 1-.434-.005c-.16 0-.34.025-.482.07-.495.155-.927.585-1.115 1.045-.21.51-.232 1.097-.232 1.604 0 .47.078.97.265 1.42.43 1.025 1.16 2.176 2.157 3.18 1.32 1.32 2.857 2.198 4.55 2.797.515.183 1.075.275 1.625.275.523 0 1.072-.078 1.547-.275.586-.243 1.156-.722 1.36-1.295.198-.557.198-1.034.143-1.135-.072-.135-.252-.208-.475-.323-.222-.115-1.42-.7-1.642-.78-.222-.08-.385-.115-.547.115z"></path>
                      <path d="M16.04 4C9.402 4 4 9.402 4 16.04c0 2.13.555 4.218 1.61 6.05L4 28l6.062-1.58A12.014 12.014 0 0 0 16.04 28C22.678 28 28 22.678 28 16.04 28 9.402 22.678 4 16.04 4zm0 21.96c-1.917 0-3.794-.515-5.43-1.49l-.39-.23-3.595.94.96-3.502-.252-.402a9.92 9.92 0 0 1-1.522-5.276C5.81 10.51 10.41 5.91 16.04 5.91s10.23 4.6 10.23 10.13c0 5.63-4.6 9.92-10.23 9.92z"></path>
                    </svg>
                    Share with your buddies
                  </button>

                  {/* T's and C's notice */}
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <Link to="/terms" className="text-xs text-gray-400 hover:text-[#FF6B35] transition-colors">
                      T's and C's apply
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contact Banner */}
        <div className="mt-12">
          <ContactBanner whatsappNumber="+27 67 535 5405" />
        </div>

        {/* As featured on KAYAK */}
        <KayakBadge className="mt-16 mb-4" />
      </div>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-[#0A2342]">
              Book Trip
            </DialogTitle>
          </DialogHeader>
          
          {selectedTrip && user && (
            <TripBookingForm
              trip={selectedTrip}
              user={user}
              onSubmit={handleBookingSubmit}
              onCancel={() => setShowBookingModal(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Inquiry Modal */}
      {inquiryTrip && (
        <TripInquiryModal
          trip={inquiryTrip}
          open={showInquiryModal}
          onOpenChange={(o) => { setShowInquiryModal(o); if (!o) setInquiryTrip(null); }}
          defaultName={user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''}
          defaultEmail={user?.email || ''}
          defaultPhone={user?.contact_number || ''}
        />
      )}
    </div>
  );
};

export default TripsPage;
